import { Box, HStack, Input, VStack } from "@chakra-ui/react";
import { DataTable } from "./data-table";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import IconAlertCircle from "../assets/icons/alert-circle.svg";
import TooltipIcon from "./tooltip-icon";
import Tooltip from "./tooltip";
import RowDragHandleCell from "./row-drag-handle-cell";
import { stringIsDisabled } from "../strings";
import { AccountingSystem, TaxType } from "../API";
import AddButton from "../routes/create-organization/add-button";
import RemoveButton from "../routes/create-organization/remove-button";
import { v4 as guid } from "uuid";
import { Select, Option } from "./select";
import { firstOfYear, getLocalDate, isValidTaxRate } from "../utils";
import DateSelector from "./date-selector";
import {
  AccountingSystemTax,
  TaxRateValue,
} from "../routes/create-organization/tax-rates";

const columnHelper = createColumnHelper<TaxRateValue>();

const taxTypes: TaxType[] = Object.values(TaxType);

const getTaxTypeName = (
  accountingSystem: AccountingSystem | undefined,
  taxType: TaxType
) => {
  switch (taxType) {
    case TaxType.investments:
      return accountingSystem === AccountingSystem.banana
        ? "Vorsteuer (Guthaben) Investitionen und Betriebsaufwand"
        : "Vorsteuer MWST Investitionen, übriger Betriebsaufwand";
    case TaxType.materials:
      return accountingSystem === AccountingSystem.banana
        ? "Vorsteuer (Guthaben) Material- und Dienstleistungsaufwand"
        : "Vorsteuer MWST Material, Waren, Dienstleistungen, Energie";
    case TaxType.earnings:
      return accountingSystem === AccountingSystem.banana
        ? "Umsatzsteuer (Schuld) Verkauf und Dienstleistungen"
        : "Geschuldete MWST (Umsatzsteuer)";
    default:
      return "";
  }
};

type TaxRatesTableProps = {
  accountingSystem: AccountingSystem | undefined;
  typeNamePlural: string;
  taxRates?: TaxRateValue[];
  hasAccountingSystemTaxes: boolean;
  accountingSystemTaxes?: AccountingSystemTax[];
  remove: <TaxRateValue>(index: number) => TaxRateValue | undefined;
  replace: <TaxRateValue>(index: number, value: TaxRateValue) => void;
  push: <X = any>(obj: X) => void;
  move: (from: number, to: number) => void;
};

const TaxRatesTable = ({
  accountingSystem,
  typeNamePlural,
  taxRates,
  hasAccountingSystemTaxes,
  accountingSystemTaxes,
  remove,
  replace,
  push,
  move,
}: TaxRatesTableProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number>();

  const optionsAccountingSystemTaxes: Option[] | undefined = useMemo(
    () =>
      accountingSystemTaxes?.map((ast) => ({
        label: ast.display_name,
        value: ast.code,
      })),
    [accountingSystemTaxes]
  );

  const optionsTaxTypes: Option[] = useMemo(
    () =>
      taxTypes.map((tt) => ({
        label: getTaxTypeName(accountingSystem, tt),
        value: tt,
      })),
    [accountingSystem]
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "drag-handle",
        cell: ({ row }) => (
          <RowDragHandleCell
            rowId={row.id}
            isDisabled={!!row.original.deletedAt}
          />
        ),
      }),
      columnHelper.accessor("code", {
        cell: (info) => {
          const isDeleted = !!info.row.original.deletedAt;
          const isValid = !!isValidTaxRate(
            hasAccountingSystemTaxes,
            accountingSystemTaxes,
            info.getValue()
          );
          const value = optionsAccountingSystemTaxes?.find(
            (o) => o.value === info.getValue()
          );
          return optionsAccountingSystemTaxes ? (
            <HStack>
              <Tooltip label={isDeleted ? stringIsDisabled : undefined}>
                <Select
                  value={value}
                  options={optionsAccountingSystemTaxes}
                  onChange={(value) => {
                    const tax = accountingSystemTaxes?.find(
                      (ast) => ast.code === value?.value
                    );
                    if (tax) {
                      replace(info.row.index, {
                        ...info.row.original,
                        code: tax.code,
                        rate: tax.value,
                        dateFrom:
                          tax.start_month && tax.start_year
                            ? new Date(tax.start_year, tax.start_month - 1)
                            : firstOfYear,
                        dateTo:
                          tax.end_month && tax.end_year
                            ? new Date(tax.end_year, tax.end_month - 1)
                            : null,
                        taxType:
                          tax.type === "pre_tax_investment"
                            ? TaxType.investments
                            : tax.type === "pre_tax_material"
                            ? TaxType.materials
                            : TaxType.earnings,
                      });
                    }
                  }}
                  isValid={isValid}
                  isDisabled={isDeleted}
                  placeholder={info.getValue()}
                  width="180px"
                />
              </Tooltip>
              {!isValid && (
                <TooltipIcon
                  header="Dieser MwSt-Satz ist in bexio nicht vorhanden."
                  body="Der gewählte Mehrwertsteuersatz ist in bexio nicht vorhanden. Bitte MwSt-Satz ändern oder löschen."
                  icon={IconAlertCircle}
                />
              )}
            </HStack>
          ) : (
            <Tooltip label={isDeleted ? stringIsDisabled : undefined}>
              <Input
                defaultValue={info.getValue()}
                onBlur={(event) => {
                  replace(info.row.index, {
                    ...info.row.original,
                    code: event.target.value,
                  });
                }}
                width="80px"
                isDisabled={isDeleted}
              />
            </Tooltip>
          );
        },
        header: "MwSt. Code",
      }),
      columnHelper.accessor("rate", {
        cell: (info) => {
          const isDeleted = !!info.row.original.deletedAt;
          return (
            <Tooltip label={isDeleted ? stringIsDisabled : undefined}>
              <Input
                type="number"
                step="0.1"
                defaultValue={info.getValue()}
                onBlur={(event) => {
                  replace(info.row.index, {
                    ...info.row.original,
                    rate: parseFloat(event.target.value) || 0,
                  });
                }}
                width="60px"
                isDisabled={isDeleted}
              />
            </Tooltip>
          );
        },
        header: "Steuersatz",
      }),
      columnHelper.accessor("dateFrom", {
        cell: (info) => {
          const isDeleted = !!info.row.original.deletedAt;
          return (
            <Tooltip label={isDeleted ? stringIsDisabled : undefined}>
              <DateSelector
                defaultValue={getLocalDate(info.getValue())}
                onChange={(date) => {
                  if (date) {
                    replace(info.row.index, {
                      ...info.row.original,
                      dateFrom: date,
                    });
                  }
                }}
                width="120px"
                isDisabled={isDeleted}
              />
            </Tooltip>
          );
        },
        header: "Gültig von",
      }),
      columnHelper.accessor("dateTo", {
        cell: (info) => {
          const isDeleted = !!info.row.original.deletedAt;
          return (
            <Tooltip label={isDeleted ? stringIsDisabled : undefined}>
              <DateSelector
                defaultValue={
                  info.getValue()
                    ? getLocalDate(info.getValue() as Date)
                    : undefined
                }
                onChange={(date) => {
                  replace(info.row.index, {
                    ...info.row.original,
                    dateTo: date ?? null,
                  });
                }}
                width="120px"
                placeholder=""
                isDisabled={isDeleted}
              />
            </Tooltip>
          );
        },
        header: "Gültig bis",
      }),
      columnHelper.accessor("taxType", {
        cell: (info) => {
          const isDeleted = !!info.row.original.deletedAt;
          const value = optionsTaxTypes.find(
            (o) => o.value === info.getValue()
          );
          return (
            <Tooltip label={isDeleted ? stringIsDisabled : undefined}>
              <Select
                value={value}
                options={optionsTaxTypes}
                onChange={(value) => {
                  replace(info.row.index, {
                    ...info.row.original,
                    taxType: value?.value as TaxType,
                  });
                }}
                isDisabled={isDeleted}
              />
            </Tooltip>
          );
        },
        header: "Steuertyp",
      }),
    ],
    [
      accountingSystemTaxes,
      hasAccountingSystemTaxes,
      optionsAccountingSystemTaxes,
      optionsTaxTypes,
      replace,
    ]
  );

  return (
    <VStack alignItems="start" width="full" spacing="16px">
      <Box
        width="full"
        minHeight={{ base: "auto", md: "418px" }}
        maxHeight="51vh"
        overflowY="auto"
        borderBottomWidth="1px"
        borderBottomColor="gray.100"
      >
        <DataTable
          typeNamePlural={typeNamePlural}
          columns={columns}
          data={taxRates}
          move={move}
          state={
            !taxRates || (hasAccountingSystemTaxes && !accountingSystemTaxes)
              ? "loading"
              : "success"
          }
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
        />
      </Box>
      <HStack>
        <AddButton<TaxRateValue>
          push={push}
          data={{
            id: guid(),
            code: "",
            rate: 0,
            dateFrom: firstOfYear,
            dateTo: null,
            taxType: TaxType.investments,
          }}
          isDisabled={!!taxRates?.find((tr) => tr.code.length === 0)}
        />
        <RemoveButton
          remove={remove}
          index={selectedIndex}
          onComplete={() =>
            setSelectedIndex(
              selectedIndex && selectedIndex > 0 ? selectedIndex - 1 : 0
            )
          }
          isDisabled={selectedIndex === undefined}
        />
      </HStack>
    </VStack>
  );
};

export default TaxRatesTable;
